import { useState } from 'react';
import type { FormEvent } from 'react';
import { subscriptions } from '@/mocks/site';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function Newsletter() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setSubmittedEmail(String(payload.get('email') || ''));
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === 'website_alt') return;
      body.append(key, String(value));
    });

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(subscriptions.submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(String(serverMsg)) || /form data is spam/i.test(responseText);
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setSubmittedEmail(String(payload.get('email') || ''));
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error ? err.message : '网络异常，请稍后再试。');
    }
  };

  return (
    <section id="subscribe" className="px-4 md:px-10 py-16 md:py-20" aria-label="订阅周报">
      <div className="rounded-lg bg-accent-100 border border-accent-300/70 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
          <div className="lg:col-span-7 p-7 md:p-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50 text-accent-900 text-xs tracking-widest">
              <i className="ri-mail-star-line"></i>
              WEEKLY DIGEST
            </span>
            <h2 className="mt-5 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
              每周一封，把这周值得看的合集打包给你
            </h2>
            <p className="mt-4 text-sm text-foreground-700 leading-relaxed max-w-xl">
              我们会把当周新增的专题、更新过的榜单和几条慢热但有用的话题线整理成一封邮件，周五傍晚发出。
            </p>

            <form
              id={subscriptions.formId}
              data-readdy-form
              action={subscriptions.submitAddr}
              method="post"
              onSubmit={handleSubmit}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hl-field-guard"
              />
              <label className="flex-1">
                <span className="sr-only">邮箱地址</span>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="请输入你的邮箱地址"
                  className="w-full h-12 px-4 rounded-md bg-background-50 border border-accent-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? '提交中…' : '订阅周报'}
              </button>
            </form>

            <div className="mt-4 min-h-[24px] text-sm">
              {status === 'success' && (
                <p className="text-secondary-800 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  订阅成功，确认邮件会发送到 {submittedEmail || '你的邮箱'}。
                </p>
              )}
              {status === 'error' && (
                <p className="text-primary-700 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 relative min-h-[220px]">
            <img
              src="https://readdy.ai/api/search-image?query=Stack%20of%20warm%20cream%20paper%20cards%20and%20a%20sealed%20envelope%20with%20amber%20wax%20stamp%20on%20sage%20green%20surface%2C%20minimal%20editorial%20still%20life%20photography%2C%20soft%20natural%20light%2C%20calm%20elegant%20mood%2C%20no%20text&width=900&height=900&seq=hl-newsletter-01&orientation=squarish"
              alt="黑料社网每周精选邮件合集示意图"
              title="每周精选邮件 专题合集"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}